import React, { useEffect, useId, useRef, useState } from "react";
import { Combobox, Dialog } from "@headlessui/react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("it");

// Render outside the calendar's scroll containers and stay inside the visible
// viewport, including when the mobile keyboard reduces its height.
const getPanelPosition = (trigger) => {
  const viewport = window.visualViewport;
  const width = viewport?.width || window.innerWidth;
  const height = viewport?.height || window.innerHeight;
  const left = viewport?.offsetLeft || 0;
  const top = viewport?.offsetTop || 0;
  const margin = 12;
  if (width < 640 || height < 520) {
    const panelWidth = Math.min(400, width - margin * 2);
    return {
      left: left + (width - panelWidth) / 2,
      top: top + margin,
      width: panelWidth,
      maxHeight: height - margin * 2,
    };
  }

  const rect = trigger.getBoundingClientRect();
  const panelWidth = Math.min(400, width - margin * 2);
  const below = top + height - rect.bottom - margin - 6;
  const above = rect.top - top - margin - 6;
  const openBelow = below >= Math.min(420, height / 2) || below >= above;
  const maxHeight = Math.min(480, openBelow ? below : above);
  return {
    left: Math.max(left + margin, Math.min(rect.left, left + width - panelWidth - margin)),
    ...(openBelow
      ? { top: rect.bottom + 6 }
      : { bottom: window.innerHeight - rect.top + 6 }),
    width: panelWidth,
    maxHeight,
  };
};

export default function VolunteerPicker({
  availableVolunteers,
  extraVolunteers,
  getCounters,
  onSelect,
  placeholder = "Aggiungi...",
  context,
  className = "",
  ...triggerProps
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [position, setPosition] = useState({});
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const dialogId = useId();

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => setPosition(getPanelPosition(triggerRef.current));
    const viewport = window.visualViewport;
    window.addEventListener("resize", updatePosition);
    viewport?.addEventListener("resize", updatePosition);
    viewport?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      viewport?.removeEventListener("resize", updatePosition);
      viewport?.removeEventListener("scroll", updatePosition);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const searchTerms = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
  const matchesSearch = (volunteer) => {
    const name = normalizeSearch(`${volunteer.nome} ${volunteer.cognome}`);
    return searchTerms.every((term) => name.includes(term));
  };
  const available = open ? availableVolunteers.filter(matchesSearch) : [];
  const extra = open ? extraVolunteers.filter(matchesSearch) : [];
  const resultCount = available.length + extra.length;

  const renderGroup = (volunteers, title, isAvailable) =>
    volunteers.length > 0 && (
      <div role="group" aria-label={title}>
        <div className="sticky top-0 z-10 border-y border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {title} <span className="ml-1 font-normal">({volunteers.length})</span>
        </div>
        {volunteers.map((volunteer) => {
          const counters = getCounters(volunteer.volontario_id);
          return (
            <Combobox.Option
              key={volunteer.volontario_id}
              value={volunteer.volontario_id}
              className={({ active }) =>
                `flex min-h-[48px] cursor-pointer items-center gap-3 px-4 py-3 text-left ${
                  active ? "bg-primary-50 text-primary-900" : "text-gray-800"
                }`
              }
            >
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm font-medium">
                  {volunteer.nome} {volunteer.cognome}
                </span>
                {!isAvailable && (
                  <span className="mt-0.5 block text-xs text-amber-700">Non disponibile</span>
                )}
              </span>
              <span className="shrink-0 rounded-md bg-white px-2 py-1 text-xs tabular-nums text-gray-600 ring-1 ring-inset ring-gray-200">
                [{counters.assegnazioni_totali} - {counters.disponibilita_totali}]
              </span>
            </Combobox.Option>
          );
        })}
      </div>
    );

  return (
    <>
      <button
        {...triggerProps}
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        aria-label={`${placeholder}${context ? ` ${context}` : ""}`}
        onClick={() => {
          setPosition(getPanelPosition(triggerRef.current));
          setOpen(true);
        }}
        className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-xs text-gray-700 shadow-sm hover:border-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${className}`}
      >
        <span className="min-w-0 break-words">{placeholder}</span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      </button>
      <Dialog id={dialogId} open={open} onClose={close} initialFocus={inputRef} className="relative z-[80]">
        <div className="fixed inset-0 bg-gray-900/25 sm:bg-gray-900/10" aria-hidden="true" />
        <Dialog.Panel
          style={position}
          onKeyDownCapture={(event) => {
            if (event.key === "Escape" && !event.nativeEvent.isComposing) {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
          className="fixed flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl"
        >
          <div className="flex shrink-0 items-start justify-between gap-2 px-4 pb-2 pt-3">
            <div className="min-w-0 py-1">
              <Dialog.Title className="text-sm font-semibold">Seleziona proclamatore</Dialog.Title>
              {context && <Dialog.Description className="mt-1 break-words text-xs text-gray-500">{context}</Dialog.Description>}
            </div>
            <button type="button" onClick={close} aria-label="Chiudi selezione" className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <Combobox
            value={null}
            onChange={(id) => {
              if (id == null) return;
              close();
              onSelect(String(id));
            }}
            className="flex min-h-0 flex-1 flex-col"
            as="div"
          >
            <div className="relative mx-3 mb-3 shrink-0">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-gray-400" aria-hidden="true" />
              <Combobox.Input
                ref={inputRef}
                aria-label="Cerca per nome o cognome"
                placeholder="Cerca nome o cognome..."
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-primary-400 focus:bg-white focus:ring-2 focus:ring-primary-100 sm:text-sm"
              />
            </div>
            <Combobox.Options static className="min-h-0 flex-1 overflow-y-auto overscroll-contain outline-none">
              {renderGroup(available, "Disponibili", true)}
              {renderGroup(extra, "Non disponibili", false)}
              {resultCount === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  Nessun nominativo trovato.
                  <span className="mt-1 block text-xs">Prova con un altro nome o cognome.</span>
                </div>
              )}
            </Combobox.Options>
          </Combobox>
          <div role="status" className="shrink-0 border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
            {resultCount} {resultCount === 1 ? "nominativo" : "nominativi"}
            <span className="ml-2">Contatori: assegnazioni - disponibilità</span>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
}
